/**
 * The Broadcasts list screen's data layer: three filters in, decoded rows and a
 * headline overview out.
 *
 * Hand-rolled useState/useEffect, matching the rest of the comms surface — this
 * app carries neither react-query nor SWR, and a hook is not the place to add a
 * dependency. The fetch idiom (cancelled flag in the effect cleanup, plus a
 * generation counter in a ref) is lifted from `BroadcastList.jsx`; the contract
 * it serves is new.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE OWNS AND WHAT IT DOES NOT
 * ---------------------------------------------------------------------------
 * It owns request orchestration: when to fire, which response to believe, and
 * what a failure means for the screen. It owns no wire names — the request
 * shape comes from `broadcastPayload.js`, the response shape from
 * `broadcastDecode.js`, and the copy for a failure from `broadcastErrors.js`.
 * Components downstream receive camelCase rows and a human error string, never
 * a raw record and never an `err.message`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { get as commsGet } from "../../../service/comms/commsApi";
import { listBroadcasts, getOverview } from "../../../service/comms/broadcastsService";
import { listFromPayload, overviewFromPayload } from "./broadcastDecode";
import { isForbidden, messageFor } from "./broadcastErrors";
import { dateRangeFromDays } from "./broadcastPayload";
import { DEFAULT_DAYS } from "./broadcastEnums";

/**
 * The "no filter" sentinel shared by the channel tabs and the status chips.
 * The service maps it away before the wire; the hook only has to pass it on.
 */
const ALL = "all";

/**
 * Load the broadcasts list plus its headline counters, scoped by channel,
 * status and a rolling day window.
 *
 * @returns {{
 *   channel: string,
 *   status: string,
 *   days: number,
 *   setChannel: (channel: string) => void,
 *   setStatus: (status: string) => void,
 *   setDays: (days: number) => void,
 *   rows: Object[],
 *   overview: Object|null,
 *   loading: boolean,
 *   error: string|null,
 *   forbidden: boolean,
 *   hasFilters: boolean,
 *   clearFilters: () => void,
 *   refresh: () => void,
 * }}
 */
export default function useBroadcastList() {
  const [channel, setChannel] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [days, setDays] = useState(DEFAULT_DAYS);

  const [rows, setRows] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  /**
   * Bumped by `refresh()` to re-run the effect with unchanged filters. A
   * counter rather than a boolean so two refreshes in a row both land.
   */
  const [refreshToken, setRefreshToken] = useState(0);

  /**
   * The generation of the most recently STARTED fetch.
   *
   * The cleanup flag below already drops a response whose effect has been torn
   * down, and on its own it would cover the common case. It is not sufficient
   * on its own here for two reasons: the flag is per-effect state that says
   * "my effect was replaced", while the ref says "someone newer is authoritative
   * now" — a distinction that matters for `refresh()`, which can re-enter
   * without a filter change, and for any future code path (a retry, a poll)
   * that starts a fetch outside the effect body. Keeping both means a late
   * response has to pass a liveness check AND a recency check, and neither one
   * silently becomes load-bearing on its own.
   */
  const generationRef = useRef(0);

  // Deliberately NOT debounced. These filters are discrete clicks on tabs,
  // chips and pills, not keystrokes: a debounce would add latency to every one
  // of them to smooth a burst that does not happen.
  useEffect(() => {
    let cancelled = false;
    const generation = generationRef.current + 1;
    generationRef.current = generation;

    /** A response is worth believing only if it is both live and current. */
    const isStale = () => cancelled || generationRef.current !== generation;

    setLoading(true);

    // Computed INSIDE the effect, not in module or render scope: a console left
    // open overnight must not keep querying yesterday's window. `now` is
    // injected so `dateRangeFromDays` itself stays pure and testable.
    const { dateFrom, dateTo } = dateRangeFromDays(days, { now: new Date() });

    // Both requests are ISSUED before either is awaited. Sequencing them would
    // make every tab click cost two round trips instead of one, and the second
    // call does not depend on the first.
    const listPromise = listBroadcasts(commsGet, { channel, status, dateFrom, dateTo });
    // The headline strip re-scopes with the tabs, so it takes the same channel.
    // It takes no status: the overview summarises a window, not a subset of it.
    const overviewPromise = getOverview(commsGet, { channel, days });

    Promise.allSettled([listPromise, overviewPromise]).then(
      ([listResult, overviewResult]) => {
        if (isStale()) return;

        if (listResult.status === "fulfilled") {
          setRows(listFromPayload(listResult.value).rows);
          setError(null);
          setForbidden(false);
        } else {
          const err = listResult.reason;
          setRows([]);
          // Never `err.message` — `messageFor` is the only place that decides
          // what a status code says to a person.
          setError(messageFor(err));
          // Kept separate from `error` on purpose: a 403 means the page renders
          // an access notice INSTEAD of its shell, where an ordinary failure
          // renders the shell with an error inside it.
          setForbidden(isForbidden(err));
        }

        // A failed overview is not a failed screen. The headline strip is a
        // nicety; the list is the page. So a rejected overview leaves
        // `overview` null and never touches `error` — only the LIST failing is
        // allowed to put an error in front of the user.
        setOverview(
          overviewResult.status === "fulfilled"
            ? overviewFromPayload(overviewResult.value)
            : null
        );

        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [channel, status, days, refreshToken]);

  /**
   * Whether the user has narrowed anything.
   *
   * This exists to tell two empty states apart. Zero rows at defaults is a
   * first run — "No broadcasts yet", with a button to write one. Zero rows with
   * filters applied is a dead end the user built and can back out of — "No
   * broadcasts match these filters", with a way to clear them. Showing the
   * first message to someone who has just filtered themselves into a corner
   * reads as data loss.
   */
  const hasFilters = channel !== ALL || status !== ALL || days !== DEFAULT_DAYS;

  const clearFilters = useCallback(() => {
    setChannel(ALL);
    setStatus(ALL);
    setDays(DEFAULT_DAYS);
  }, []);

  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  return {
    channel,
    status,
    days,
    setChannel,
    setStatus,
    setDays,
    rows,
    overview,
    loading,
    error,
    forbidden,
    hasFilters,
    clearFilters,
    refresh,
  };
}
